import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EscrowTimeline, { escrowLabel } from "@/components/EscrowTimeline";
import { toast } from "sonner";
import { Loader2, PackageCheck, ShoppingBag } from "lucide-react";

export default function BuyerDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => api.get("/orders/my").then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async (id) => {
    setActing(id);
    try {
      await api.post(`/orders/${id}/confirm-delivery`);
      toast.success("Delivery confirmed. The artist will be paid.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setActing(null); }
  };

  const active = orders.filter((o) => !["pending_payment", "cancelled"].includes(o.escrow_status));
  const spent = active.reduce((s, o) => s + o.amount, 0);

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="overline-badge">Collector Dashboard</div>
        <h1 className="font-serif text-4xl mt-2">Welcome, {user?.name?.split(" ")[0]}</h1>

        <div className="grid sm:grid-cols-3 gap-6 mt-8">
          {[["Active orders", active.length], ["Total invested", money(spent)],
            ["Trade tier", user?.trade_verified ? `${user.rebate_discount_pct}% + ${money(user.rebate_credit)}` : "Standard"]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-2xl border border-[var(--border-subtle)] p-6">
              <div className="text-xs text-[var(--text-muted)]">{l}</div>
              <div className="font-serif text-3xl mt-1">{v}</div>
            </div>
          ))}
        </div>

        <h2 className="font-serif text-2xl mt-12 mb-6">Your orders</h2>
        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[var(--gold)]" /></div>
          : orders.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-[var(--border-subtle)]" data-testid="buyer-empty">
              <ShoppingBag className="w-10 h-10 mx-auto text-[var(--text-muted)]" />
              <p className="text-[var(--text-muted)] mt-3">No orders yet.</p>
              <Link to="/gallery" className="btn-gold inline-block mt-4 px-6 py-2.5 rounded-full text-sm font-semibold">Explore the gallery</Link>
            </div>
          ) : (
            <div className="space-y-5">
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-2xl border border-[var(--border-subtle)] p-6" data-testid={`buyer-order-${o.id}`}>
                  <div className="flex flex-col md:flex-row gap-5">
                    <img src={o.artwork_image} alt="" className="w-full md:w-28 h-28 object-cover rounded-lg" />
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-serif text-xl">{o.artwork_title}</h3>
                          <p className="text-sm text-[var(--text-muted)]">by {o.artist_name}</p>
                        </div>
                        <div className="text-right">
                          <div className="font-mono">{money(o.amount)}</div>
                          <div className="escrow-badge mt-1">{escrowLabel(o.escrow_status)}</div>
                        </div>
                      </div>
                      {o.tracking_number && (
                        <p className="text-xs text-[var(--text-muted)] mt-2 font-mono">{o.carrier} · {o.tracking_number}</p>
                      )}
                    </div>
                  </div>
                  {o.escrow_status !== "pending_payment" && (
                    <div className="mt-6 pt-6 border-t border-[var(--border-subtle)]"><EscrowTimeline status={o.escrow_status} /></div>
                  )}
                  {o.escrow_status === "shipped" && (
                    <button onClick={() => confirm(o.id)} disabled={acting === o.id} data-testid={`confirm-delivery-${o.id}`}
                      className="btn-gold mt-5 px-6 py-3 rounded-full text-sm font-semibold flex items-center gap-2">
                      {acting === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />} Confirm delivery
                    </button>
                  )}
                  {o.escrow_status === "pending_payment" && (
                    <p className="text-sm text-amber-700 mt-4">Payment not completed. Start a new purchase from the gallery.</p>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
      <Footer />
    </div>
  );
}
